import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

const FEATURE_INFO = {
  rgm_pro: {
    title: "RGM Pro",
    icon: "👑",
    blurb: "Pro tools for serious artists: deeper analytics, priority support, advanced profile customization, and early-access features.",
    cta: "Notify me when Pro launches",
  },
  promote_track: {
    title: "Promote Track",
    icon: "🚀",
    blurb: "Pay to push your track into Discover, Charts, Featured Artists and the weekly digest. Pricing tiers + targeting coming soon.",
    cta: "Tell me when promotion is live",
  },
  featured_placement: {
    title: "Featured Placement",
    icon: "✨",
    blurb: "Reserve a homepage / Magazine cover slot. A&R-curated, high-visibility placements for artists ready to break out.",
    cta: "Notify me when slots open",
  },
};

const MonetizationWaitlistDialog = ({ open, onClose, feature, defaultEmail = "" }) => {
  const info = FEATURE_INFO[feature] || FEATURE_INFO.rgm_pro;
  const [email, setEmail] = useState(defaultEmail);
  const [note, setNote] = useState("");
  const [count, setCount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) { setDone(false); setNote(""); return; }
    setEmail(defaultEmail || "");
    api.get("/monetization/waitlist/count", { params: { feature } })
      .then(r => setCount(r.data?.count ?? 0))
      .catch(() => setCount(null));
  }, [open, feature, defaultEmail]);

  const submit = async () => {
    const e = (email || "").trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/monetization/waitlist", { feature, email: e, note: note.trim() || null });
      toast.success(r.data?.message || "You're on the list!");
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not join waitlist");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="monetization-waitlist-dialog">
      <div className="w-full max-w-md bg-[#161620] border border-purple-500/40 rounded-xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{info.icon}</span>
            <div>
              <h2 className="text-xl font-bold text-white">{info.title}</h2>
              <p className="text-xs text-purple-300">Coming soon</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl" aria-label="Close">×</button>
        </div>
        <p className="text-sm text-gray-300 mb-4">{info.blurb}</p>
        {count !== null && count > 0 && (
          <p className="text-xs text-emerald-300 mb-3" data-testid="waitlist-count">
            {count} {count === 1 ? "artist is" : "artists are"} on the waitlist.
          </p>
        )}

        {done ? (
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm text-emerald-200" data-testid="waitlist-done">
            🎉 You're on the list — we'll email you when it launches.
          </div>
        ) : (
          <div className="space-y-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-black/40 text-white border-gray-700"
              data-testid="waitlist-email-input"
            />
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional: what would you use this for?"
              className="bg-black/40 text-white border-gray-700"
              data-testid="waitlist-note-input"
            />
            <Button
              onClick={submit}
              disabled={submitting}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white"
              data-testid="waitlist-submit-button"
            >
              {submitting ? "Joining…" : info.cta}
            </Button>
            <p className="text-[11px] text-gray-500 text-center">
              No payments processed yet. We'll only email you about this feature.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonetizationWaitlistDialog;
